import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { Building2, LogOut, Menu, Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../../lib/auth";
import { useMe } from "../../lib/me";
import { Button } from "../../ui/Button";
import { installSoundUnlock } from "../alerts/audio";
import { SoundLockedHint } from "../alerts/SoundLockedHint";
import { ServerHealthWidget } from "../platform/ServerHealthWidget";

export function AppLayout() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const adminOrg = me.memberships.find((m) => m.role === "admin");
  // On a phone the org name and the nav buttons collapse into a hamburger menu (#290). On desktop
  // (lg+) everything is inline and this flag is ignored.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(installSoundUnlock, []);

  const logout = async () => {
    await authClient.signOut();
    queryClient.clear();
    await navigate({ to: "/login" });
  };

  const nav = (
    <>
      {me.platformAdmin && (
        <Link to="/organizations" onClick={() => setMenuOpen(false)}>
          <Button size="sm" variant="ghost">
            <Building2 size={14} /> {t("organizations.title")}
          </Button>
        </Link>
      )}
      {adminOrg && (
        <Link
          to="/organizations/$organizationId/members"
          params={{ organizationId: adminOrg.organizationId }}
          onClick={() => setMenuOpen(false)}
        >
          <Button size="sm" variant="ghost">
            <Users size={14} /> {t("members.title")}
          </Button>
        </Link>
      )}
      <span className="px-2 text-zinc-400 lg:px-0">{me.user.name}</span>
      <Button size="sm" variant="ghost" onClick={() => void logout()} title={t("auth.signOut")}>
        <LogOut size={14} /> <span className="lg:hidden">{t("auth.signOut")}</span>
      </Button>
    </>
  );

  return (
    <div className="flex h-screen flex-col bg-zinc-950 text-zinc-100">
      <header className="flex h-11 shrink-0 items-center gap-3 border-b border-zinc-800 px-3 text-sm">
        <Link to="/" className="shrink-0 font-semibold tracking-wide">
          Ratun
        </Link>
        {/* The org name can be long; truncate on a phone so it never pushes the controls off-screen. */}
        <span className="min-w-0 truncate text-zinc-500">
          {me.memberships.map((m) => m.organizationName).join(", ")}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <SoundLockedHint />
          {/* Desktop: the nav inline. Phone: a hamburger that opens the same nav as a dropdown. */}
          <div className="hidden items-center gap-2 lg:flex">{nav}</div>
          <Button
            size="sm"
            variant="ghost"
            className="lg:hidden"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            title={t("common.menu")}
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </Button>
        </div>
      </header>
      {menuOpen && (
        <div className="flex flex-col items-stretch gap-1 border-b border-zinc-800 bg-zinc-950 p-2 lg:hidden">
          {nav}
        </div>
      )}
      <div className="relative min-h-0 flex-1">
        <Outlet />
        {me.platformAdmin && <ServerHealthWidget />}
      </div>
    </div>
  );
}
